import { useEffect, useState } from 'react';
import { strings } from '@/content/strings';
import { settingsStore } from '@/game/settings';
import { qa, useQa } from './recorder';
import { useElapsed } from './useElapsed';
import styles from './QaPanel.module.css';

/**
 * Grown-ups: QA test sessions (docs/design/qa-recording.md). Start records the microphone, what
 * the app says and its decisions; Stop saves the session and uploads it to the developer's
 * computer when reachable. Saved sessions can be uploaded, downloaded or deleted.
 */
export function QaPanel() {
  const t = strings.qa;
  const state = useQa();
  const elapsed = useElapsed(state.startedAt);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    void qa.recover();
  }, []);

  const run = (action: () => Promise<unknown>) => {
    setBusy(true);
    void action().finally(() => {
      setBusy(false);
    });
  };

  return (
    <section className={styles.panel} aria-labelledby="qa-title">
      <h3 id="qa-title" className={styles.title}>
        {t.title}
      </h3>
      {state.recording ? (
        <>
          <p className={styles.recording}>
            <span className={styles.dot} aria-hidden />
            {t.recording(elapsed)}
          </p>
          <label className={styles.noteLabel}>
            {t.noteLabel}
            <textarea
              className={styles.note}
              value={note}
              rows={2}
              placeholder={t.notePlaceholder}
              onChange={(e) => {
                setNote(e.target.value);
              }}
            />
          </label>
          <button
            type="button"
            className={styles.stop}
            disabled={busy}
            onClick={() => {
              run(async () => {
                await qa.stop(note.trim() || undefined);
                setNote('');
              });
            }}
          >
            {t.stop}
          </button>
        </>
      ) : (
        <>
          <p className={styles.text}>{t.explain}</p>
          <button
            type="button"
            className={styles.start}
            disabled={busy}
            onClick={() => {
              run(() =>
                qa.start({ age: settingsStore.get().age, route: window.location.hash || '#/' }),
              );
            }}
          >
            {t.start}
          </button>
        </>
      )}
      {state.status && <p className={styles.status}>{state.status}</p>}
      {state.pending.length > 0 && (
        <ul className={styles.pending} aria-label={t.pending}>
          {state.pending.map((s) => (
            <li key={s.manifest.id}>
              <span>{s.manifest.id}</span>
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  run(() => qa.upload(s.manifest.id));
                }}
              >
                {t.upload}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  run(async () => {
                    const blob = await qa.zip(s.manifest.id);
                    const a = document.createElement('a');
                    a.href = URL.createObjectURL(blob);
                    a.download = `foe-names-qa-${s.manifest.id}.zip`;
                    a.click();
                    setTimeout(() => {
                      URL.revokeObjectURL(a.href);
                    }, 10_000);
                  });
                }}
              >
                {t.download}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  run(() => qa.remove(s.manifest.id));
                }}
              >
                {t.remove}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
